import { Avatar, Box, Typography } from '@mui/material';
import { getAttachment } from '@utils/getAttachment';
import { Artifact, PostArtifact } from '@models/note';
import { instanceOfINewArtifact } from '@utils/fileUtils';
import { TypographyHighlighter } from '@components/typography/TypographyHighlighter';
import { InvalidFile } from '@assets/svgs';
import { useMemo } from 'react';
import { ellipsis } from '@theme/styles';

const containerStyle = {
  display: 'flex',
  width: '100%',
  overflowX: 'hidden',
  overflowY: 'hidden',
} as const;

const childContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  marginLeft: '8px',
  width: 'min-content',
  overflowX: 'hidden',
} as const;

const fileStyle = {
  '.image': {
    backgroundColor: 'warning.main',
  },
  '.word': {
    backgroundColor: 'externalIcon.word',
  },
  '.excel': {
    backgroundColor: 'externalIcon.excel',
  },
  '.powerPoint': {
    backgroundColor: 'error.light',
  },
  '.pdf': {
    backgroundColor: 'error.dark',
  },
  '.video': {
    backgroundColor: 'secondary.main',
  },
  '.invalid': {
    backgroundColor: 'common.gray',
  },
} as const;

interface IAttachmentFile {
  searchBarText?: string;
  variant?: 'regular' | 'dense';
}

export const AttachmentFile = (props: IAttachmentFile & (Artifact | PostArtifact)) => {
  const fileName = useMemo(() => {
    if (!instanceOfINewArtifact(props)) {
      const { description, source } = props;
      if (props?.fileName) return props?.fileName;
      if (description && description !== '') return description;
      if (source.dmsId) {
        const path = source.dmsId.split('/');
        return path[path.length - 1];
      }
    }
    return '';
  }, [props]);

  if (instanceOfINewArtifact(props)) {
    const { name, mimetype, invalidMsg } = props;
    const fileType = mimetype?.split('/')[1];
    let attachmentFile;
    if (invalidMsg) {
      attachmentFile = {
        key: 'file',
        value: 'File',
        Icon: InvalidFile,
      };
    } else {
      attachmentFile = getAttachment(fileType);
    }
    const { key, value, Icon } = attachmentFile;

    return (
      <Box sx={[containerStyle, fileStyle]}>
        {invalidMsg ? (
          <Avatar className='invalid' variant='rounded'>
            <Icon />
          </Avatar>
        ) : (
          <Avatar className={key} variant='rounded'>
            <Icon />
          </Avatar>
        )}
        <Box sx={childContainerStyle}>
          <Typography role='heading' aria-level={9} variant='bodySmall' sx={{ ...ellipsis }}>
            {name}
          </Typography>
          <Typography
            role='heading'
            aria-level={7}
            variant='caption'
            sx={[{ color: invalidMsg ? 'error.main' : 'text.subdued', ...ellipsis }]}
          >
            {invalidMsg || value}
          </Typography>
        </Box>
      </Box>
    );
  }

  const { source, searchBarText, variant } = props;
  let fileType = source?.dmsId?.split('.')[1];
  if (!fileType && props?.fileName) {
    // if for some reason dmsId does not result in a filetype, try to get from filename
    const fileParts = props?.fileName?.split('.');
    fileType = fileParts[fileParts.length - 1];
  }
  const attachmentFile = getAttachment(fileType || '');

  if (!attachmentFile) return null;
  const { key, value, Icon } = attachmentFile;

  return (
    <Box sx={[containerStyle, fileStyle]} title={`icon-${key}`}>
      <Avatar
        className={key}
        variant='rounded'
        sx={
          variant === 'dense'
            ? {
                borderRadius: 2,
                width: 30,
                height: 30,
              }
            : {}
        }
      >
        <Icon />
      </Avatar>
      <Box sx={childContainerStyle}>
        <TypographyHighlighter
          text={fileName}
          highlightText={searchBarText}
          role='heading'
          aria-level={9}
          variant='bodySmall'
          sx={{ ...ellipsis, pr: 2 }}
        />
        {variant !== 'dense' && (
          <Typography role='heading' aria-level={7} variant='caption' sx={[{ color: 'text.subdued', ...ellipsis }]}>
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
